// CRM Mailbox Detail — "sender cockpit". Hero band leads with the two things that
// matter: health (SVG ring gauge) and today's sending pace vs cap (fill bar with
// ramp trajectory). Settings demoted to a quiet two-column grid; connection details
// to a metadata strip; danger to a single line. Orange stays action-only; the ring
// uses semantic health colours (green/amber/rose).
// Route: /clients/:id/crm/deliverability/mailboxes/:mailboxId
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import API_BASE from '../../config';

const PROVIDER_LABEL = { google: 'Google', microsoft: 'Microsoft 365', zoho: 'Zoho', smtp: 'SMTP / IMAP' };

function fmt(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
         d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function ago(v) {
  if (!v) return null;
  const d = new Date(v); if (isNaN(d)) return null;
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  const days = Math.round(hrs / 24);
  return days + 'd ago';
}

function healthColour(score) {
  if (score == null) return 'var(--crm-slate)';
  if (score >= 80) return 'var(--crm-green)';
  if (score >= 50) return 'var(--crm-amber)';
  return 'var(--crm-rose)';
}
function healthWord(score) {
  if (score == null) return 'Not scored';
  if (score >= 80) return 'Healthy';
  if (score >= 50) return 'Warming';
  return 'At risk';
}

function HealthRing({ score }) {
  const size = 96, stroke = 9, r = (size - stroke) / 2, circ = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const colour = healthColour(score);
  return (
    <div className="crmmb-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--crm-line)" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none" stroke={colour} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset .6s ease' }}
        />
      </svg>
      <div className="crmmb-ring-c">
        <div className="crmmb-ring-n" style={{ color: colour }}>{score == null ? '—' : score}</div>
        <div className="crmmb-ring-l">health</div>
      </div>
    </div>
  );
}

export default function CrmMailboxDetail() {
  const { mailboxId } = useParams();
  const { clientId } = useOutletContext();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [mb, setMb] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [cap, setCap] = useState('');
  const [ceiling, setCeiling] = useState('');
  const [ramp, setRamp] = useState('');
  const [warmup, setWarmup] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [sigHtml, setSigHtml] = useState('');
  const [sigText, setSigText] = useState('');

  const backToList = `/clients/${clientId}/crm/deliverability`;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}`, { headers });
      if (!res.ok) throw new Error(`Failed to load mailbox (${res.status})`);
      const data = await res.json();
      setMb(data);
      setCap(data.daily_cap ?? '');
      setCeiling(data.daily_cap_ceiling ?? '');
      setRamp(data.ramp_pct ?? '');
      setWarmup(!!data.warmup_enabled);
      setDisplayName(data.display_name || '');
      setSigHtml(data.signature_html || '');
      setSigText(data.signature_text || '');
      fetch(`${API_BASE}/mailboxes/${mailboxId}/health`, { headers })
        .then(r => r.ok ? r.json() : null).then(h => setHealth(h)).catch(() => {});
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [mailboxId, getToken]);

  useEffect(() => { load(); }, [load]);

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      toast.success(msg);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const saveSending = () => patch({ daily_cap: parseInt(cap) || 0, daily_cap_ceiling: parseInt(ceiling) || 0, ramp_pct: parseInt(ramp) || 0, warmup_enabled: warmup }, 'Sending settings saved');
  const saveIdentity = () => patch({ display_name: displayName, signature_html: sigHtml, signature_text: sigText }, 'Identity saved');
  const setStatus = (status) => patch({ status }, status === 'active' ? 'Activated' : 'Paused');

  const reconnect = () => {
    const cid = mb?.client_id || clientId;
    window.open(`${API_BASE}/oauth/${mb.provider}/connect?client_id=${cid}&mailbox_id=${mailboxId}`, '_blank', 'noopener');
    toast('Finish signing in on the new tab, then refresh.', { icon: '↗' });
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${mb.email_address}? It stops sending and detaches from every campaign. This can't be undone.`)) return;
    setBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/mailboxes/${mailboxId}?hard=true`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      toast.success('Mailbox deleted');
      navigate(backToList);
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  if (loading) return <div className="crmmb"><div className="crmmb-loading">Loading mailbox…</div></div>;
  if (error) return <div className="crmmb"><div className="crmmb-error">{error}</div></div>;
  if (!mb) return <div className="crmmb"><div className="crmmb-loading">Mailbox not found.</div></div>;

  const score = health?.score ?? mb.health_score ?? null;
  const sent = mb.sent_today ?? 0;
  const capNum = parseInt(cap) || mb.daily_cap || 0;
  const ceilNum = parseInt(ceiling) || mb.daily_cap_ceiling || 0;
  const pacePct = capNum > 0 ? Math.min(100, Math.round((sent / capNum) * 100)) : 0;
  const capOfCeil = ceilNum > 0 ? Math.min(100, Math.round((capNum / ceilNum) * 100)) : 0;
  const isOAuth = mb.provider !== 'smtp';
  const statusCls = mb.status === 'active' ? 'ok' : mb.status === 'error' ? 'err' : mb.status === 'paused' ? 'warn' : 'idle';

  return (
    <div className="crmmb">
      <button className="crmmb-back" onClick={() => navigate(backToList)}>← Deliverability</button>

      <div className="crmmb-hero">
        <HealthRing score={score} />
        <div className="crmmb-hero-main">
          <div className="crmmb-hero-top">
            <h1 className="crmmb-email">{mb.email_address}</h1>
            <span className={'crmmb-status crmmb-status--' + statusCls}>
              <span className="crmmb-status-dot" />{mb.status}
            </span>
          </div>
          <div className="crmmb-hero-meta">
            <span>{PROVIDER_LABEL[mb.provider] || mb.provider}</span>
            <span className="crmmb-sep">·</span>
            <span>{healthWord(score)}</span>
            {mb.last_sync_at && <><span className="crmmb-sep">·</span><span>synced {ago(mb.last_sync_at)}</span></>}
            {mb.last_error && <><span className="crmmb-sep">·</span><span className="crmmb-err-inline">{mb.last_error}</span></>}
          </div>

          {mb.assigned_clients && mb.assigned_clients.length > 0 && (
            <div className="crmmb-clients">
              <span className="crmmb-clients-l">Used by</span>
              {mb.assigned_clients.map(c => (
                <button key={c.id} className="crmmb-client-chip" onClick={() => navigate(`/clients/${clientId}/crm`)} title={c.sector || ''}>{c.name}</button>
              ))}
            </div>
          )}

          <div className="crmmb-pace">
            <div className="crmmb-pace-track">
              <div className="crmmb-pace-cap" style={{ width: capOfCeil + '%' }} title={`Cap ${capNum} of ceiling ${ceilNum}`} />
              <div className="crmmb-pace-sent" style={{ width: pacePct + '%' }} />
            </div>
            <div className="crmmb-pace-lbl">
              <strong>{sent}</strong> sent today of <strong>{capNum}</strong> cap
              {ceilNum > capNum && <span className="crmmb-pace-ceil"> · ramping to {ceilNum}</span>}
            </div>
          </div>
        </div>

        <div className="crmmb-hero-actions">
          {mb.status === 'active'
            ? <button className="crmmb-btn" disabled={busy} onClick={() => setStatus('paused')}>Pause</button>
            : <button className="crmmb-btn crmmb-btn--primary" disabled={busy} onClick={() => setStatus('active')}>Activate</button>}
          {isOAuth && <button className="crmmb-btn" disabled={busy} onClick={reconnect}>Reconnect</button>}
        </div>
      </div>

      <div className="crmmb-grid">
        <section className="crmmb-panel">
          <div className="crmmb-panel-h">Sending</div>
          <div className="crmmb-fields3">
            <label className="crmmb-field"><span className="crmmb-field-l">Daily cap</span><input className="crmmb-in" type="number" value={cap} onChange={e => setCap(e.target.value)} /></label>
            <label className="crmmb-field"><span className="crmmb-field-l">Ceiling</span><input className="crmmb-in" type="number" value={ceiling} onChange={e => setCeiling(e.target.value)} /></label>
            <label className="crmmb-field"><span className="crmmb-field-l">Ramp %/day</span><input className="crmmb-in" type="number" value={ramp} onChange={e => setRamp(e.target.value)} /></label>
          </div>
          <label className="crmmb-toggle">
            <input type="checkbox" checked={warmup} onChange={e => setWarmup(e.target.checked)} />
            <span>Warmup enabled</span>
            <span className="crmmb-toggle-hint">gradually raises the cap toward the ceiling</span>
          </label>
          <button className="crmmb-btn crmmb-btn--primary crmmb-panel-save" disabled={busy} onClick={saveSending}>Save sending</button>
        </section>

        <section className="crmmb-panel">
          <div className="crmmb-panel-h">Identity</div>
          <label className="crmmb-field"><span className="crmmb-field-l">Display name</span><input className="crmmb-in" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Name recipients see" /></label>
          <label className="crmmb-field"><span className="crmmb-field-l">Signature (HTML)</span><textarea className="crmmb-ta" value={sigHtml} onChange={e => setSigHtml(e.target.value)} rows={4} /></label>
          <label className="crmmb-field"><span className="crmmb-field-l">Signature (plain text)</span><textarea className="crmmb-ta" value={sigText} onChange={e => setSigText(e.target.value)} rows={3} /></label>
          <button className="crmmb-btn crmmb-btn--primary crmmb-panel-save" disabled={busy} onClick={saveIdentity}>Save identity</button>
        </section>
      </div>

      {health && (health.deliverability != null || health.bounce_rate != null || health.spam_rate != null) && (
        <div className="crmmb-metrics">
          {health.deliverability != null && <div className="crmmb-metric"><div className="crmmb-metric-n">{health.deliverability}</div><div className="crmmb-metric-l">Deliverability</div></div>}
          {health.bounce_rate != null && <div className="crmmb-metric"><div className="crmmb-metric-n">{health.bounce_rate}%</div><div className="crmmb-metric-l">Bounce rate</div></div>}
          {health.spam_rate != null && <div className="crmmb-metric"><div className="crmmb-metric-n">{health.spam_rate}%</div><div className="crmmb-metric-l">Spam rate</div></div>}
        </div>
      )}

      <div className="crmmb-strip">
        <span className="crmmb-strip-k">Provider</span><span>{PROVIDER_LABEL[mb.provider] || mb.provider} · {mb.auth_type}</span>
        {mb.smtp_host && <><span className="crmmb-strip-k">SMTP</span><span>{mb.smtp_host}:{mb.smtp_port}</span></>}
        {mb.imap_host && <><span className="crmmb-strip-k">IMAP</span><span>{mb.imap_host}:{mb.imap_port}</span></>}
        <span className="crmmb-strip-k">Connected</span><span>{fmt(mb.connected_at)}</span>
      </div>

      <div className="crmmb-danger">
        <span>Delete this mailbox and detach it from every campaign.</span>
        <button className="crmmb-btn crmmb-btn--danger" disabled={busy} onClick={remove}>Delete mailbox</button>
      </div>
    </div>
  );
}
